import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { glassBackdrop, glassModal, glassInput } from './liquidGlass';

export interface RosterPlayer {
  playerId: string;
  name: string;
  position: string | null;
  jerseyNumber: number | null;
}

interface RosterPickerSheetProps {
  open: boolean;
  onClose: () => void;
  teamId: string | null | undefined;
  /** Called when the coach taps a player to drop on the canvas. The
   *  parent should mutate the active TacticsLayout (or drill_spec) to
   *  add a spot at pitch centre with the picked player's jersey + position. */
  onPick: (player: RosterPlayer) => void;
  /** Already-placed player IDs — these show as "Already placed" pills
   *  so the coach can spot duplicates. */
  alreadyPlacedIds?: ReadonlySet<string>;
}

/**
 * Roster picker — mirrors web1's RosterPickerButton UX. Slides over the
 * canvas as a modal sheet; lists active team players with name + position
 * + jersey number; tap to place on the pitch.
 */
export function RosterPickerSheet({
  open,
  onClose,
  teamId,
  onPick,
  alreadyPlacedIds,
}: RosterPickerSheetProps): React.JSX.Element | null {
  const [roster, setRoster] = useState<RosterPlayer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (!open || !teamId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      const { data, error: err } = await supabase
        .from('team_players')
        .select('player_id, position, jersey_number, users!inner(name)')
        .eq('team_id', teamId)
        .eq('is_active', true)
        .limit(200);
      if (cancelled) return;
      if (err) {
        setError(err.message);
        setRoster([]);
        setLoading(false);
        return;
      }
      const rows: RosterPlayer[] = (data ?? []).map((r: any) => ({
        playerId: r.player_id as string,
        name: (r.users?.name as string | undefined) ?? 'Unnamed',
        position: (r.position as string | null) ?? null,
        jerseyNumber: (r.jersey_number as number | null) ?? null,
      }));
      rows.sort((a, b) => {
        // Sort by jersey if both have one, otherwise alphabetical.
        if (a.jerseyNumber != null && b.jerseyNumber != null) return a.jerseyNumber - b.jerseyNumber;
        if (a.jerseyNumber != null) return -1;
        if (b.jerseyNumber != null) return 1;
        return a.name.localeCompare(b.name);
      });
      setRoster(rows);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, teamId]);

  // Filter on name / position / jersey number.
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return roster;
    return roster.filter((p) =>
      p.name.toLowerCase().includes(q) ||
      (p.position?.toLowerCase().includes(q) ?? false) ||
      (p.jerseyNumber != null && String(p.jerseyNumber).includes(q))
    );
  }, [roster, filter]);

  // Group by position for the list — falls back to "Unassigned".
  const grouped = useMemo(() => {
    const map = new Map<string, RosterPlayer[]>();
    for (const p of filtered) {
      const k = p.position ?? 'Unassigned';
      const list = map.get(k) ?? [];
      list.push(p);
      map.set(k, list);
    }
    // Common positional order; anything not in this list lands alphabetically.
    const order = ['GK', 'CB', 'LB', 'RB', 'LWB', 'RWB', 'CDM', 'CM', 'CAM', 'LM', 'RM', 'LW', 'RW', 'CF', 'ST', 'Unassigned'];
    const orderedKeys = [...map.keys()].sort((a, b) => {
      const ai = order.indexOf(a);
      const bi = order.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
    return orderedKeys.map((k) => [k, map.get(k)!] as const);
  }, [filtered]);

  if (!open) return null;

  return (
    <div role="dialog" aria-label="Roster picker" style={backdropStyle} onClick={onClose}>
      <div style={sheetStyle} onClick={(e) => e.stopPropagation()}>
        <header style={headerStyle}>
          <h2 style={{ margin: 0, fontSize: 16, color: '#fff' }}>Pick from roster</h2>
          <button
            type="button"
            onClick={onClose}
            style={closeBtnStyle}
            aria-label="Close roster picker"
          >
            ✕
          </button>
        </header>
        <div style={{ padding: '0 16px 12px' }}>
          <input
            type="text"
            placeholder="Filter by name, position, jersey"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={inputStyle}
            autoFocus
          />
        </div>
        <div style={listWrapStyle}>
          {loading && <p style={statusStyle}>Loading roster…</p>}
          {error && <p style={{ ...statusStyle, color: '#c0392b' }}>Couldn't load: {error}</p>}
          {!loading && !error && roster.length === 0 && (
            <p style={statusStyle}>No active players on this team yet. Add some on /players first.</p>
          )}
          {grouped.map(([positionKey, players]) => (
            <section key={positionKey} style={groupStyle}>
              <div style={groupHeaderStyle}>{positionKey} · {players.length}</div>
              <ul style={listStyle}>
                {players.map((p) => {
                  const already = alreadyPlacedIds?.has(p.playerId) ?? false;
                  return (
                    <li key={p.playerId}>
                      <button
                        type="button"
                        onClick={() => onPick(p)}
                        style={already ? alreadyBtn : rowBtn}
                        title={already ? 'Already on the pitch — placing again will create a duplicate' : 'Place this player on the pitch'}
                      >
                        <span style={jerseyStyle}>{p.jerseyNumber ?? '—'}</span>
                        <span style={{ flex: 1, textAlign: 'left' }}>{p.name}</span>
                        {p.position && <span style={posPillStyle}>{p.position}</span>}
                        {already && <span style={{ fontSize: 10, opacity: 0.6 }}>placed</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

// -- styles -----------------------------------------------------------------

const backdropStyle: React.CSSProperties = {
  ...glassBackdrop,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const sheetStyle: React.CSSProperties = {
  ...glassModal,
  width: 'min(520px, calc(100vw - 32px))',
  maxHeight: 'calc(100vh - 64px)',
  display: 'flex',
  flexDirection: 'column',
  fontFamily: 'system-ui, sans-serif',
};

const headerStyle: React.CSSProperties = {
  padding: '14px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
};

const closeBtnStyle: React.CSSProperties = {
  background: 'transparent',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.2)',
  borderRadius: 6,
  width: 32,
  height: 32,
  cursor: 'pointer',
};

const inputStyle: React.CSSProperties = glassInput;

const listWrapStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '0 16px 16px',
};

const statusStyle: React.CSSProperties = {
  fontSize: 13,
  opacity: 0.8,
  textAlign: 'center',
  padding: 24,
};

const groupStyle: React.CSSProperties = {
  marginBottom: 16,
};

const groupHeaderStyle: React.CSSProperties = {
  fontSize: 11,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  opacity: 0.6,
  marginBottom: 6,
};

const listStyle: React.CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const rowBtn: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  padding: '8px 10px',
  background: 'rgba(255,255,255,0.04)',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 13,
};

const alreadyBtn: React.CSSProperties = {
  ...rowBtn,
  opacity: 0.6,
};

const jerseyStyle: React.CSSProperties = {
  width: 30,
  height: 30,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#2980B9',
  color: '#fff',
  borderRadius: 999,
  fontWeight: 700,
  fontSize: 12,
  flexShrink: 0,
};

const posPillStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.08)',
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: 11,
  opacity: 0.85,
};
